import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    border: '1px solid #356AC3',
    marginTop: theme.spacing(1),
    minWidth: 250,
    color: 'black',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiListSubheader-root': {
      lineHeight: '32px',
      paddingLeft: '12px',
      textAlign: 'left',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
    },
    '& .MuiMenuItem-root': {
      fontSize: 14,
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

// Owner/editor-only "Sort by" dropdown.
//
// `sections` is an array of { label?, options: [{ key, label, comparator }] }.
//   - Resource lists pass two sections ("General", "Category Specific").
//   - Workbench tabs pass a single label-less section (a flat list).
// A section's heading is shown only when it has a `label`; empty sections and
// the whole control are hidden when there is nothing to sort by.
export default function SortByMenu({ sections, onSelect }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const visibleSections = (sections || []).filter((s) => s.options && s.options.length > 0);
  if (visibleSections.length === 0) return null;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handlePick = (option) => {
    handleClose();
    onSelect(option);
  };

  const menuChildren = [];
  visibleSections.forEach((section, index) => {
    if (index > 0) menuChildren.push(<Divider key={`divider-${index}`} sx={{ my: 0.5 }} />);
    if (section.label) {
      menuChildren.push(<ListSubheader key={`header-${index}`}>{section.label}</ListSubheader>);
    }
    section.options.forEach((option) =>
      menuChildren.push(
        <MenuItem key={option.key} disableRipple onClick={() => handlePick(option)}>
          {option.label}
        </MenuItem>
      )
    );
  });

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disableRipple
        startIcon={<SwapVertIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          textTransform: 'none',
          borderColor: '#356AC3',
          color: '#356AC3',
          borderRadius: '6px',
          py: '2px',
          '&:hover': { borderColor: '#356AC3', backgroundColor: alpha('#356AC3', 0.04) },
        }}
      >
        Sort by
      </Button>
      <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {menuChildren}
      </StyledMenu>
    </>
  );
}
